import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const TRUST_PILLARS = [{
  id: 1,
  title: 'Nurse-Vetted Standard',
  description: 'Every worker is screened by a registered nurse. We verify credentials, check references, and assess clinical competency.',
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?w=600&auto=format&fit=crop'
}, {
  id: 2,
  title: 'Worker-First Philosophy',
  description: 'Better pay and flexibility means better retention and better patient outcomes. Workers choose their shifts and set rates.',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&auto=format&fit=crop'
}, {
  id: 3,
  title: 'Regulated & Compliant',
  description: 'Fully compliant with provincial and federal healthcare regulations. All workers carry valid registration and insurance.',
  image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&auto=format&fit=crop'
}, {
  id: 4,
  title: 'Community-Driven',
  description: 'A portion of every transaction funds nursing scholarships and mental health support for healthcare workers.',
  image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=600&auto=format&fit=crop'
}];
export function TrustSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Handle responsive cards to show
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCardsToShow(1);
      } else if (window.innerWidth < 1024) {
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const nextSlide = useCallback(() => {
    if (!isTransitioning) return;
    setCurrentIndex(prev => prev + 1);
  }, [isTransitioning]);
  const prevSlide = () => {
    if (!isTransitioning) return;
    setCurrentIndex(prev => prev - 1);
  };

  // Auto-play
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isHovered, nextSlide]);

  // Handle infinite snap-back
  useEffect(() => {
    if (currentIndex >= TRUST_PILLARS.length) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
      }, 500); // Match transition duration
      return () => clearTimeout(timeout);
    }
    if (currentIndex < 0) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(TRUST_PILLARS.length - 1);
      }, 500);
      return () => clearTimeout(timeout);
    }
    if (!isTransitioning) {
      // Force a reflow and re-enable transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
    }
  }, [currentIndex, isTransitioning]);

  // Create an extended array for the infinite loop visual
  const extendedPillars = [...TRUST_PILLARS, ...TRUST_PILLARS, ...TRUST_PILLARS];
  return <section id="why-us" className="w-full bg-gray-50 py-16 lg:py-24 relative overflow-hidden" aria-label="Trust and Safety" data-id="element-617" data-mp="9c4dyvp1v9ag">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-618" data-mp="3ctw1nxw5l72u">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#007a87]/5 rounded-full blur-3xl" data-id="element-619" data-mp="3qsvtfeeyq238"></div>
        <div className="absolute bottom-0 left-[-10%] w-full max-w-lg h-64 bg-[#5a55ea]/5 rounded-full blur-3xl" data-id="element-620" data-mp="3moxryx260f70"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-621" data-mp="163pwii3wt4ok">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12" data-id="element-622" data-mp="1200ao0emd178">
          <div className="max-w-2xl" data-id="element-623" data-mp="1xc9i0n9o2mdb">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a2332] tracking-tight mb-4" data-id="element-624" data-mp="21bs2jhxksvfm">
              Because quality care <span className="text-[#007a87]" data-id="element-625" data-mp="26a3b7pc4wzw9">matters most</span>
            </h2>
            <p className="text-lg text-[#5a6a7a] leading-relaxed" data-id="element-626" data-mp="2bar4khnu5stn">
              At Care Nomad, quality isn't just a pledge—it's built into everything
              we do. Every professional is vetted and managed by a Registered Nurse.
            </p>
          </div>
          
          {/* Slider Controls */}
          <div className="flex items-center gap-3" data-id="element-627" data-mp="2p2nqg3ywv6hl">
            <button onClick={prevSlide} className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#1a2332] hover:bg-[#007a87] hover:text-white hover:border-[#007a87] transition-all shadow-sm" aria-label="Previous slide" data-id="element-628" data-mp="2p3yum4w9qg98">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-629" data-mp="sc2vhcvqko5s" />
            </button>
            <button onClick={nextSlide} className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#1a2332] hover:bg-[#007a87] hover:text-white hover:border-[#007a87] transition-all shadow-sm" aria-label="Next slide" data-id="element-630" data-mp="s96zhu3g89jz">
              <ChevronRightIcon className="w-6 h-6" data-id="element-631" data-mp="1b1rnax4ajogx" />
            </button>
          </div>
        </div>

        {/* Slider Container */}
        <div className="relative overflow-hidden -mx-4 px-4" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-632" data-mp="1649agvn5kp3n">
          <div className="flex" style={{
          transform: `translateX(-${(currentIndex + TRUST_PILLARS.length) * (100 / cardsToShow)}%)`,
          transition: isTransitioning ? 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
        }} data-id="element-633" data-mp="wdwqp5n5wzyk">
            {extendedPillars.map((pillar, index) => <div key={`${pillar.id}-${index}`} className="flex-none px-3 lg:px-4" style={{
            width: `${100 / cardsToShow}%`
          }} data-id="element-634" data-mp="3jc0lnkscvu11" data-mpi={index}>
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden hover:shadow-xl transition-all duration-300 group" data-id="element-635" data-mp="39i0ivpkjr0vh">
                  <div className="h-48 w-full bg-gray-200 overflow-hidden relative" data-id="element-636" data-mp="30hllvsy7zxc5">
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-300 z-10" data-id="element-637" data-mp="34ljhhtoc6q0g"></div>
                    <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-638" data-mp="2qnbr64lt2my9" />
                  </div>
                  <div className="p-8 flex flex-col flex-1" data-id="element-639" data-mp="ts6z2makbw67">
                    <h3 className="text-xl font-bold text-[#1a2332] mb-3" data-id="element-640" data-mp="2ea817gszqpec">
                      {pillar.title}
                    </h3>
                    <p className="text-[#5a6a7a] leading-relaxed text-sm flex-1" data-id="element-641" data-mp="29d7wb97vnsot">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}